/**
 * Live master-bus capture — ScriptProcessor tap on the engine output node.
 * Returns mono mixdown PCM suitable for the sample library.
 */
export type BusRecorder = {
  stop: () => Float32Array;
  /** Per-block RMS of the mono mix (0..~1) for meters. */
  getLevel: () => number;
};

/**
 * Tap `source` (engine master) in parallel with the speakers.
 * Keeps a silent sink so the ScriptProcessor stays in the graph.
 */
export function startBusCapture(ctx: AudioContext, source: AudioNode): BusRecorder {
  if (ctx.state === "suspended") {
    void ctx.resume();
  }
  const chunks: Float32Array[] = [];
  let level = 0;

  // ScriptProcessor is deprecated but fine for capture; 2 in → stereo, mix to mono.
  const bufferSize = 4096;
  const processor = ctx.createScriptProcessor(bufferSize, 2, 1);
  processor.onaudioprocess = event => {
    const left = event.inputBuffer.getChannelData(0);
    const right =
      event.inputBuffer.numberOfChannels > 1
        ? event.inputBuffer.getChannelData(1)
        : left;
    const n = left.length;
    const mix = new Float32Array(n);
    let sumSq = 0;
    for (let i = 0; i < n; i++) {
      const s = (left[i]! + right[i]!) * 0.5;
      mix[i] = s;
      sumSq += s * s;
    }
    chunks.push(mix);
    level = Math.sqrt(sumSq / Math.max(1, n));
    event.outputBuffer.getChannelData(0).fill(0);
  };

  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  processor.connect(mute);
  mute.connect(ctx.destination);

  return {
    getLevel() {
      return level;
    },
    stop() {
      try {
        source.disconnect(processor);
      } catch {
        // ignore — already disconnected
      }
      try {
        processor.disconnect();
        mute.disconnect();
      } catch {
        // ignore
      }
      let total = 0;
      for (const c of chunks) {
        total += c.length;
      }
      const out = new Float32Array(total);
      let offset = 0;
      for (const c of chunks) {
        out.set(c, offset);
        offset += c.length;
      }
      return out;
    },
  };
}
